import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { onboardingConstants } from '@/lib';
import { CustomButton, Loader } from '@/components/common';
import { requestDocumentSignLinkAsync, requestDocumentViewLinkAsync } from '@/redux/contracts';
import { connect, useDispatch } from 'react-redux';
import { documentViewLinkLoadingSelector } from '@/redux/loading';
import { isAdminSelector } from '@/redux/auth';
import { CustomErrorMessage } from '@/components/common/Form';
import classNames from 'classnames';
import { useFeatureFlag } from 'configcat-react';
import { IFrameModal } from "@/components/modals";

const {
  I9_UPLOAD_TEXT,
  DOCUMENT_UPLOAD_BUTTON,
  DOCUMENT_CONTINUE_BUTTON,
  DOCUMENT_VIEW_BUTTON,
  DOCUMENT_TYPE_I9,
  I9_CLICKED,
  I9_ENVELOPE_SENT_NAME,
  I9_SUBMITTED_NAME,
  DOCUMENT_UPLOAD_NEW_BUTTON,
  I9_HELP_TEXT,
  DOCUMENT_IN_REVIEW_TEXT,
  I9_COMPLETED_NAME,
  I9_DISABLED_AFTER_COMPLETION_FEATURE_FLAG_NAME,
} = onboardingConstants;

const I9Upload = ({
  isPersonalWizard,
  canEditField,
  userId,
  viewLoading,
  isAdmin,
}) => {
  const { setValue, getValues, formState: { errors } } = useFormContext();
  const [signLoading, setSignLoading] = useState(false);
  const [isIframeShown, setIsIframeShown] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);

  const openLinkAsIFrame = (link) => {
    setIsIframeShown(true);
    setIframeUrl(link);
  };

  const openLinkAsPopUp = (link) => {
    window.open(link, '_blank')?.focus();
  };

  const onViewLinkClosed = () => {
    setIsIframeShown(false);
  }

  const { value: disableUploadWhenI9Completed } = useFeatureFlag(
    I9_DISABLED_AFTER_COMPLETION_FEATURE_FLAG_NAME,
    false,
  );

  const dispatch = useDispatch();

  const {
    [I9_SUBMITTED_NAME]: i9Submitted,
    [I9_ENVELOPE_SENT_NAME]: i9EnvelopeSent,
    [I9_COMPLETED_NAME]: i9Completed,
  } = getValues();

  const signButtonText = useMemo(() => {
    if (i9Submitted) {
      return DOCUMENT_UPLOAD_NEW_BUTTON;
    } else if (i9EnvelopeSent) {
      return DOCUMENT_CONTINUE_BUTTON;
    } else {
      return DOCUMENT_UPLOAD_BUTTON;
    }
  }, [i9EnvelopeSent, i9Submitted]);

  const onSignLinkLoaded = ({ link }) => {
    setValue(I9_CLICKED, true, { shouldValidate: true });
    setValue(I9_COMPLETED_NAME, false, { shouldValidate: true });
    setValue(I9_ENVELOPE_SENT_NAME, true, { shouldValidate: true });
    openLinkAsPopUp(link);
    setSignLoading(false);
  };

  const onViewLinkLoaded = ({ link }) => {
    openLinkAsIFrame(link);
    setSignLoading(false);
  };

  const onViewClick = () => {
    setSignLoading(true);
    dispatch(requestDocumentViewLinkAsync.request({
      documentType: DOCUMENT_TYPE_I9,
      userId: userId,
      callback: onViewLinkLoaded,
    }));
  };

  const onSignClick = (event) => {
    if (!isPersonalWizard) {
      event.preventDefault();

      return;
    }

    setSignLoading(true);
    dispatch(requestDocumentSignLinkAsync.request({
      documentType: DOCUMENT_TYPE_I9,
      callback: onSignLinkLoaded,
      errorCallback: () => setSignLoading(false),
    }));
  };

  const isUploadDisabled = (disableUploadWhenI9Completed && i9Completed)
    || !canEditField(DOCUMENT_TYPE_I9)
    || !isPersonalWizard;

  return (
    <div>
      <IFrameModal
        onClose={onViewLinkClosed}
        isOpen={isIframeShown}
        url={iframeUrl}
        title='I9: Employment Eligibility Verification'
      />
      <div className="flex justify-between items-center space-x-6 border border-gray-200 px-6 py-8">
        <div>
          {'*'}{I9_UPLOAD_TEXT}
          {i9Submitted && !i9Completed && (
            <div className="text-sm text-red-500">
              {DOCUMENT_IN_REVIEW_TEXT}
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-center items-center">
          {i9Completed && i9EnvelopeSent && i9Submitted && (
            <>
              {viewLoading ? (
                <Loader className="m-2" />
              ) : (
                <CustomButton
                  color="green"
                  onClick={onViewClick}
                  disabled={!isPersonalWizard && !isAdmin}
                  className="m-1 px-6 py-2 text-gray-700 font-normal shadow-none text-white"
                >
                  {DOCUMENT_VIEW_BUTTON}
                </CustomButton>
              )}
            </>
          )}
          {signLoading ? (
            <Loader />
          ) : (
            <CustomButton
              color="green"
              onClick={onSignClick}
              disabled={isUploadDisabled}
              className={classNames(
                'my-1 px-6 py-2 text-gray-700 font-normal shadow-none text-white',
                { 'text-gray-300': isUploadDisabled },
              )}
            >
              {signButtonText}
            </CustomButton>
          )}
        </div>
      </div>
      <div className="mt-2 text-sm text-gray-500 italic">
        {i9EnvelopeSent && !i9Submitted ? I9_HELP_TEXT : null}
      </div>
      {errors?.i9Clicked?.message && (
        <CustomErrorMessage className="w-full" text={errors?.i9Clicked?.message} />
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    viewLoading: documentViewLinkLoadingSelector(state),
    isAdmin: isAdminSelector(state),
  };
};

export default connect(mapStateToProps)(I9Upload);
